import { ET_AUTH_ERROR_MESSAGES, etAuthErrorMessage } from "../../lib/et-auth-errors";
import { sanitizeTabKeys, type TabKey } from "../../lib/tab-registry";

/**
 * Dashboard IAM calls. Every call goes to our server, which checks the `iam`
 * tab and forwards to et-auth with the service token; et-auth re-checks the
 * caller's `iam` row and enforces the self-protection and last-holder rules.
 * Those rules protect recovery, not scope: `iam` is full power by design, and
 * a holder may grant any tab to any admin, themselves included.
 */

export type AdminAccount = {
  id: number;
  email: string;
  name: string | null;
  username: string | null;
  displayUsername: string | null;
  accountRole: string | null;
  banned: boolean;
  createdAt: string;
  permissions: TabKey[];
  lastChangedAt: string | null;
  lastChangedBy: number | null;
};

export type NewAdmin = {
  email: string;
  password: string;
  name: string;
  username?: string;
  displayUsername?: string;
  permissions: TabKey[];
};

export type BulkAdminResult =
  | { email: string; ok: true; userId: number }
  | { email: string; ok: false; error: string };

export type PromoteCandidate = {
  id: string;
  email: string;
  name: string | null;
  username: string | null;
};

const jsonHeaders = { "Content-Type": "application/json" };

/**
 * The shared et-auth code sentences, with the two this tab says differently:
 * FORBIDDEN here always means the caller does not hold Dashboard IAM, and an
 * existing email is promoted rather than created. et-auth creates an admin as
 * a plain account first, so a create that failed after that step leaves an
 * account this sentence points to.
 */
export const IAM_ERROR_MESSAGES: Record<string, string> = {
  ...ET_AUTH_ERROR_MESSAGES,
  FORBIDDEN: "You do not hold Dashboard IAM.",
  ALREADY_EXISTS: "An account with that email already exists. Use Promote existing user instead.",
};

/** The sentence for an et-auth error body, or a bulk-create row's code. */
export const iamErrorMessage = (
  body: { error?: unknown; detail?: unknown } | null | undefined,
  fallback: string,
): string => {
  const code = typeof body?.error === "string" ? body.error : undefined;
  if (code && IAM_ERROR_MESSAGES[code]) return IAM_ERROR_MESSAGES[code] as string;
  return etAuthErrorMessage(body, fallback);
};

/**
 * Tab ids from et-auth, cleaned against the registry before React renders
 * them. A stored id the registry does not know is dropped, so one stale row
 * cannot white-screen the tab.
 */
const cleanPermissions = <T>(value: T): T => {
  if (Array.isArray(value)) return value.map(cleanPermissions) as unknown as T;
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = { ...(value as Record<string, unknown>) };
    for (const [key, item] of Object.entries(out)) {
      out[key] = key === "permissions" ? sanitizeTabKeys(item) : cleanPermissions(item);
    }
    return out as T;
  }
  return value;
};

const readError = async (res: Response, fallback: string): Promise<string> => {
  try {
    return iamErrorMessage((await res.json()) as { error?: unknown; detail?: unknown }, fallback);
  } catch {
    return fallback;
  }
};

const request = async <T>(path: string, init: RequestInit, fallback: string): Promise<T> => {
  const res = await fetch(path, { credentials: "include", ...init });
  if (!res.ok) throw new Error(await readError(res, fallback));
  const text = await res.text();
  return cleanPermissions((text ? JSON.parse(text) : {}) as T);
};

export const iamApi = {
  listAdmins: (query: { limit: number; offset: number; query?: string }) => {
    const params = new URLSearchParams({ limit: String(query.limit), offset: String(query.offset) });
    if (query.query) params.set("query", query.query);
    return request<{ admins: AdminAccount[]; total: number }>(
      `/api/internal/admin/iam/admins?${params}`,
      { method: "GET" },
      "Failed to load admins",
    );
  },

  replacePermissions: (userId: number, permissions: TabKey[]) =>
    request<{ userId: number; permissions: TabKey[] }>(
      `/api/internal/admin/iam/users/${userId}/permissions`,
      { method: "PUT", headers: jsonHeaders, body: JSON.stringify({ permissions }) },
      "Failed to update permissions",
    ),

  createAdmin: (input: NewAdmin) =>
    request<{ userId: number; permissions: TabKey[] }>(
      "/api/internal/admin/iam/admins",
      { method: "POST", headers: jsonHeaders, body: JSON.stringify(input) },
      "Failed to create admin",
    ),

  bulkCreateAdmins: (users: NewAdmin[]) =>
    request<{ results: BulkAdminResult[] }>(
      "/api/internal/admin/iam/admins/bulk",
      { method: "POST", headers: jsonHeaders, body: JSON.stringify({ users }) },
      "Failed to create admins",
    ),

  searchCandidates: (query: string) =>
    request<{ users: PromoteCandidate[] }>(
      `/api/internal/admin/iam/candidates?query=${encodeURIComponent(query)}`,
      { method: "GET" },
      "Failed to search users",
    ),

  promote: (userId: number, permissions: TabKey[]) =>
    request<{ userId: number; permissions: TabKey[] }>(
      `/api/internal/admin/iam/users/${userId}/promote`,
      { method: "POST", headers: jsonHeaders, body: JSON.stringify({ permissions }) },
      "Failed to promote user",
    ),

  demote: (userId: number) =>
    request<{ userId: number }>(
      `/api/internal/admin/iam/users/${userId}/demote`,
      { method: "POST" },
      "Failed to demote admin",
    ),
};
